import { JournalEntry } from '@/types';

interface ExportJournalParams {
  entries: JournalEntry[];
  totals: {
    totalDebit: number;
    totalCredit: number;
    totalBaseDebit: number;
    totalBaseCredit: number;
    totalTaxAmount: number;
    bankTotal: number;
    revenueTotal: number;
    balance: number;
  };
  batchName: string;
  journal: string;
  postDate: string;
  description: string;
  location: string;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, '-');
}

function calculateBaseAmount(amount: number | null | undefined, taxAmount: number | null | undefined): number | null {
  const lineAmount = amount ?? 0;
  if (lineAmount <= 0) {
    return null;
  }

  return Math.round((lineAmount + Math.abs(taxAmount ?? 0)) * 100) / 100;
}

function calculateDisplayedTaxAmount(taxAmount: number | null | undefined): number | null {
  if (taxAmount === null || taxAmount === undefined) {
    return null;
  }

  return Math.round(Math.abs(taxAmount) * 100) / 100;
}

export async function exportJournalToExcel(params: ExportJournalParams): Promise<void> {
  const { entries, totals, batchName, journal, postDate, description, location } = params;

  // Dynamic import for code-splitting - only loads when user clicks Export
  const { default: writeXlsxFile } = await import('write-excel-file/browser');

  const numberCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return null;
    }
    return { type: Number, value, format: '#,##0.00' };
  };

  const textCell = (value: unknown) => ({ type: String, value: value == null ? '' : String(value) });

  const rows: unknown[][] = [];

  // Section A: Header metadata
  rows.push(['Batch', batchName]);
  rows.push(['Journal', journal]);
  rows.push(['Post Date', postDate]);
  rows.push(['Description', description]);
  rows.push(['Location', location]);
  rows.push([]); // Empty separator row

  // Section B: Column headers
  const headers = [
    'Journal Type', 'Memo', 'Account #', 'Account Name',
    'Department ID', 'Project ID', 'Class ID', 'PFP Code', 'Contribution Code',
    'Debit', 'Credit', 'Base Debit', 'Base Credit', 'Tax ID', 'Tax Amount',
  ];
  rows.push(headers);

  // Data rows
  for (const entry of entries) {
    rows.push([
      entry.journalType,
      entry.memo,
      textCell(entry.accountNumber),
      entry.accountName,
      entry.departmentId,
      entry.projectId,
      entry.classId,
      entry.pfpCode,
      entry.contributionCode,
      numberCell(entry.debit),
      numberCell(entry.credit),
      numberCell(calculateBaseAmount(entry.debit, entry.taxAmount)),
      numberCell(calculateBaseAmount(entry.credit, entry.taxAmount)),
      entry.taxId,
      numberCell(calculateDisplayedTaxAmount(entry.taxAmount)),
    ]);
  }

  // Section C: Totals
  rows.push([]); // Empty separator row
  // TOTALS row - align amounts with their columns
  const totalsRow: unknown[] = [
    'TOTALS', null, null, null, null, null, null, null, null,
    null, null, numberCell(totals.totalBaseDebit), numberCell(totals.totalBaseCredit), null, numberCell(totals.totalTaxAmount),
  ];
  rows.push(totalsRow);
  rows.push(['Bank Total', numberCell(totals.bankTotal)]);
  rows.push(['Revenue Total', numberCell(totals.revenueTotal)]);
  rows.push(['Tax Subtotal', numberCell(totals.totalTaxAmount)]);
  rows.push(['Balance', numberCell(totals.balance)]);

  const filename = `Journal_${sanitizeFilename(batchName)}_${postDate}.xlsx`;

  const result = writeXlsxFile([{
    sheet: 'Journal Entries',
    data: rows as any,
    columns: [
      { width: 16 }, // A: Journal Type
      { width: 35 }, // B: Memo
      { width: 14 }, // C: Account #
      { width: 30 }, // D: Account Name
      { width: 16 }, // E: Department ID
      { width: 16 }, // F: Project ID
      { width: 16 }, // G: Class ID
      { width: 16 }, // H: PFP Code
      { width: 20 }, // I: Contribution Code
      { width: 15 }, // J: Debit
      { width: 15 }, // K: Credit
      { width: 15 }, // L: Base Debit
      { width: 15 }, // M: Base Credit
      { width: 28 }, // N: Tax ID
      { width: 15 }, // O: Tax Amount
    ],
  }]);

  await result.toFile(filename);
}
